/**
 * School-year labels for UK schools: which year group a leaving year means
 * today, and the "Leaving 2027 · Year 13" label staff and students see.
 *
 * Mirrors backend/app/services/school_years.py, which is the source of the
 * rule: the academic year starts on 1 September and is named by the
 * calendar year it ends in, and a student is in Year 13 during the academic
 * year they leave. Years outside 7-13 have no year group.
 *
 * Use the server's `year_group` wherever a payload carries one; this module
 * is for payloads that carry only a leaving year (the scope picker).
 * "Today" is Europe/London's date, as on the server, so the label does not
 * change at a different midnight for a teacher abroad.
 */

const ACADEMIC_YEAR_START_MONTH = 9; // September, 1-based
const FIRST_YEAR_GROUP = 7;
const FINAL_YEAR_GROUP = 13;

/** Europe/London's calendar date as { year, month } (month 1-12). */
export function londonToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(now);
  const part = (type) => Number(parts.find((p) => p.type === type).value);
  return { year: part('year'), month: part('month') };
}

/** The calendar year the academic year containing `today` ends in. */
export function academicYearEnd(today) {
  return today.month >= ACADEMIC_YEAR_START_MONTH ? today.year + 1 : today.year;
}

/** Year group for a student leaving in `cohortYear`, or null outside 7-13. */
export function yearGroupFor(cohortYear, today) {
  const group = FINAL_YEAR_GROUP - (cohortYear - academicYearEnd(today));
  return group >= FIRST_YEAR_GROUP && group <= FINAL_YEAR_GROUP ? group : null;
}

/** "Leaving 2027 · Year 13", or "Leaving 2027" when there is no year group. */
export function leavingLabel(cohortYear, yearGroup) {
  return yearGroup == null ? `Leaving ${cohortYear}` : `Leaving ${cohortYear} · Year ${yearGroup}`;
}
